import { UTCDateMini } from "@date-fns/utc";
import {
  useMemberCalculateProRataForResumingSubscription,
  useMemberSubscription,
  useQueryMemberPaymentMethodListNew,
} from "@gymflow/api";
import {
  dateStringToDate,
  formatCurrency,
  lastSecondOfYesterday,
  paymentMethodHelper,
  utcToZonedTime,
} from "@gymflow/helpers";
import { UserMemberSubscriptionBeanWithMembership } from "@gymflow/types";
import { useQuery } from "@tanstack/react-query";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { isSameMonth } from "date-fns";
import React, { useCallback, useContext } from "react";

import { ApiResolverContext } from "../../../../providers/ApiResolverProvider/ApiResolverContext";
import { ConfirmModal } from "../../../templates";
import { NotPrimaryMembershipWarning } from "../NotPrimaryMembershipWarning";

export interface ResumeMembershipModalProps {
  memberId: string;
  membership: UserMemberSubscriptionBeanWithMembership;
}

export const ResumeMembershipModal: React.FC<ResumeMembershipModalProps> = ({
  memberId,
  membership,
}) => {
  const { club } = useClubSettings();
  const { clubId } = useContext(ApiResolverContext);
  const { api } = useGymflowModels();
  const calculateProRataMutation =
    useMemberCalculateProRataForResumingSubscription({
      api,
      tz: club.timezone,
    });
  const { data: paymentMethods } = useQueryMemberPaymentMethodListNew({
    api,
    clubId,
    memberId,
  });
  const defaultPaymentMethod = paymentMethods?.find(
    (pm) =>
      pm.defaultPaymentMethod && !paymentMethodHelper.isBankPaymentMethod(pm),
  );
  const { data: prorata } = useQuery({
    queryKey: ["prorata", membership.id, memberId],
    queryFn: async () => {
      const result = await calculateProRataMutation.mutateAsync({
        subscriptionId: membership.id,
        resumeDate: lastSecondOfYesterday(club.timezone),
        memberId,
      });
      return result > 0 ? result : 0;
    },
  });

  const { hide } = useContext(ModalContext);

  const { resumeSubscriptionEarlyMutation } = useMemberSubscription({
    api,
    tz: club.timezone,
  });
  const showScaModal = useScaModal({
    asMember: false,
  });

  const resumeSubscriptionByUser = useCallback(
    async (isWaived: boolean) => {
      const result = (await resumeSubscriptionEarlyMutation.mutateAsync({
        ...(isWaived === true
          ? { isWaived: true }
          : {
              isWaived: false,
              paymentMethodId: defaultPaymentMethod?.id,
            }),
        resumeDate: lastSecondOfYesterday(club.timezone),
        subscriptionId: membership.id,
        memberId,
      })) as any;

      if (result.data?.invoice?.number) {
        showScaModal({
          invoiceNumber: result.data.invoice.number,
          userMemberId: memberId,
        });
      }
    },
    [
      defaultPaymentMethod?.id,
      memberId,
      membership.id,
      resumeSubscriptionEarlyMutation,
      showScaModal,
      club.timezone,
    ],
  );
  const isChargeable = !(
    (membership.pauseStartDate &&
      isSameMonth(
        dateStringToDate(
          utcToZonedTime(membership.pauseStartDate, club.timezone),
        ),
        new UTCDateMini(),
      )) ||
    prorata === undefined ||
    prorata < 0.5
  );
  return (
    <ConfirmModal
      onConfirm={async () => {
        if (isChargeable) {
          await resumeSubscriptionByUser(false);
        } else {
          await resumeSubscriptionByUser(true);
        }
        hide();
      }}
      title="Resume Membership"
      onCancel={async () => {
        if (isChargeable) {
          await resumeSubscriptionByUser(true);
        }
        hide();
      }}
      onHide={async () => {
        hide();
      }}
      confirmText={isChargeable ? "Charge" : "Resume"}
      cancelText={isChargeable ? "Waive" : "Close"}
    >
      <div className="flex flex-col gap-4">
        <div>Memberships will not be billed while paused.</div>
        <div>Members can resume at anytime in the members app.</div>
        {membership.pauseStartDate &&
        isSameMonth(
          dateStringToDate(
            utcToZonedTime(membership.pauseStartDate, club.timezone),
          ),
          new UTCDateMini(),
        ) ? (
          <div>
            You&apos;re resuming this membership in the same month it was paused
            so there aren&apos;t any fees to collect.
          </div>
        ) : prorata !== undefined ? (
          prorata < 0.5 ? (
            <div>
              Due to the prorata being below the minimum allowed, you can only
              Waive the fees.
            </div>
          ) : (
            <div>
              Membership Pro-rata due:{" "}
              {formatCurrency(prorata, club.defaultCurrency)}
            </div>
          )
        ) : undefined}
        <NotPrimaryMembershipWarning membership={membership} />
      </div>
    </ConfirmModal>
  );
};
